import React, {useCallback, useEffect, useState} from "react";
import {Grid, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm} from "react-hook-form";
import {Application, applicationSchema, translationsOfNameComplete} from "./Application";
import {handleFormError, RhfForm} from "../../shared/components/forms/RhfForm";
import {FormHeader} from "../../shared/components/forms/FormHeader";
import {yupResolver} from "@hookform/resolvers/yup";
import Typography from "@mui/material/Typography";
import {TranslatedValuesForm} from "../../shared/technical/i18n/TranslatedValuesForm";
import {useAccount} from "../users/AccountProvider";
import {mayEditApplication} from "./permissions";
import {ApplicationSubPageContentContainer} from "./ApplicationSubPageContentContainer";

export function ApplicationTranslationsSubPage(props: {
  application: Application,
  handleSave: (application: Application) => Promise<Application>,
}) {
  const {handleSave} = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const account = useAccount();
  const [formReadonly, setFormReadonly] = useState<boolean>(!!props.application.id);
  const [application, setApplication] = useState<Application>(props.application);
  const methods = useForm<Application>({
    mode: 'onBlur',
    defaultValues: application,
    resolver: yupResolver(applicationSchema(t))
  });
  const {register, setError} = methods;

  useEffect(() => {
    register('id');
  }, [register]);

  useEffect(() => {
    setApplication(props.application);
  }, [props.application]);

  const saveApplication = useCallback((application: Application) => {
    handleSave(application).then(() => setFormReadonly(true), (error) => {
      handleFormError(error, setError, t);
    });
  }, [handleSave, setError, t])

  return (
      <ApplicationSubPageContentContainer>
        <FormProvider {...methods} >
          <RhfForm id={'application-form'} onSubmit={saveApplication}>
            <FormHeader title={t('ApplicationPage.subPageTranslations')}
                        onCancel={() => {
                          setApplication(props.application);
                          setFormReadonly(true);
                        }}
                        onDelete={() => {
                        }}
                        canBeEdited={mayEditApplication(account, props.application)}
                        readonly={formReadonly}
                        onStartEditing={() => setFormReadonly(false)}
                        hideDialogControls={true}
            />
            <Grid container={true} sx={{padding: theme.spacing(1), width: '100%'}}>
              {!translationsOfNameComplete(application) && application.id &&
                  <Grid item={true} xs={12} sx={{marginTop: 1}}>
                      <Typography color={"error"} variant={"body2"}>{t('Application.translationsMissingMessage')}</Typography>
                  </Grid>
              }
              <Grid item={true} xs={12} lg={12}>
                <Grid container={true} spacing={3}>
                  <Grid item={true} xs={12} sx={{marginTop: theme.spacing(1),}}>
                    <TranslatedValuesForm name={'displayNames'} disabled={formReadonly} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {!formReadonly &&
                <Typography variant={"caption"}>{t('Application.updateMessage')}</Typography>}
          </RhfForm>
        </FormProvider>
      </ApplicationSubPageContentContainer>
  )
}
